"use client";

import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";
import { cn } from "@/lib/utils";

// Sistema de avisos transitorios (toasts) propio, sin dependencias externas
// (el proyecto prefiere minimizarlas). Un único <ToastProvider> en el layout expone
// `useToast()` con `success`/`error`/`info`; cada aviso se autodescarta a los 4 s y
// puede cerrarse a mano. Sustituye al feedback inline disperso (textos grises "Guardado").

type ToastVariant = "success" | "error" | "info";
type Toast = { id: number; message: string; variant: ToastVariant };

type ToastApi = {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

const DURACION_MS = 4000;

const ESTILO: Record<ToastVariant, { icon: typeof Info; color: string }> = {
  success: { icon: CheckCircle2, color: "text-emerald-600 dark:text-emerald-400" },
  error: { icon: AlertCircle, color: "text-destructive" },
  info: { icon: Info, color: "text-primary" },
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((ts) => ts.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (variant: ToastVariant, message: string) => {
      const id = nextId.current++;
      setToasts((ts) => [...ts, { id, message, variant }]);
      setTimeout(() => dismiss(id), DURACION_MS);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (m) => push("success", m),
      error: (m) => push("error", m),
      info: (m) => push("info", m),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {toasts.length > 0 && (
        <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 p-4 sm:items-end sm:p-6">
          {toasts.map((toast) => {
            const { icon: Icon, color } = ESTILO[toast.variant];
            return (
              <div
                key={toast.id}
                role="status"
                className="pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border bg-card px-4 py-3 shadow-lg animate-in fade-in slide-in-from-bottom-2"
              >
                <Icon className={cn("mt-0.5 size-5 shrink-0", color)} />
                <p className="flex-1 text-sm text-foreground">{toast.message}</p>
                <button
                  type="button"
                  onClick={() => dismiss(toast.id)}
                  className="-mr-1 -mt-0.5 rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                  aria-label="Cerrar"
                >
                  <X className="size-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast debe usarse dentro de <ToastProvider>");
  return ctx;
}
